import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Wrapper from '../../components/Wrapper';
import FarmersTable from '../../components/tables/FarmersTable';
import SupportPagination from '../../components/utils/supportPagination';
import PartnerPagination from '../../components/utils/partnerPagination';
import { getRole } from '../../utils/role';
import {
  fetchUsers,
  setCurrentPage,
} from '../../store/features/usersSlice';
import { selectCurrentRole } from '../../store/features/roleSlice';
import { hasPermission } from '../../hooks/usePermission';
import { FaPlus, FaSearch } from 'react-icons/fa';

const Users = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const role = useSelector(selectCurrentRole) ?? getRole();

  const {
    users,
    allUsers,
    status,
    currentPage,
    totalPages,
  } = useSelector((state) => state.users);

  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  
  // Client-side pagination state (Partner & Field Officer)
  const [clientPage, setClientPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  /* -------- Fetch only when role is ready to avoid multiple/duplicate GET /admin/users -------- */
  useEffect(() => {
    if (!role) return;

    dispatch(fetchUsers({
      page: currentPage,
      limit: 15,
      keyword:
        role === 'SUPPORT' && submittedSearch
          ? submittedSearch
          : undefined,
    }));
  }, [currentPage, dispatch, role, submittedSearch]);

  /* Search Handler */
  const handleSearchButton = () => {
    if (role !== 'SUPPORT') return;

    setSubmittedSearch(search.trim());

    if (currentPage !== 1) {
      dispatch(setCurrentPage(1));
    }
  };

  const isClientSidePagination = (role === 'PARTNER' || role === 'TEAM_MEMBER') || role === 'FIELD_OFFICER';

  const filteredClientUsers = isClientSidePagination
    ? allUsers.filter((u) =>
        Object.values(u).some((v) =>
          v?.toString().toLowerCase().includes(search.toLowerCase())
        )
      )
    : [];

  const indexOfLast = clientPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const clientUsers = filteredClientUsers.slice(indexOfFirst, indexOfLast);

  const tableData = role === 'SUPPORT' ? users : clientUsers;

  return (
    <Wrapper>
      <div className="reports-container py-4 w-100">
        <div className="d-flex flex-column flex-md-row justify-content-between mb-3">
          <h1 className="fw-bolder">Farmers</h1>

          <div className="d-flex flex-column flex-lg-row align-items-stretch align-items-lg-center gap-3 mt-3 mt-md-0 w-100 justify-content-lg-end">
            {/* Search Bar */}
            <div className="input-group flex-grow-1" style={{ maxWidth: "420px" }} >
              <input
                type="text"
                className="form-control"
                placeholder="Search..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  if (isClientSidePagination) setClientPage(1); // Reset page on filter
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchButton()}
              />
              {role === 'SUPPORT' && (
                <button className="btn btn-outline-secondary" onClick={handleSearchButton}>
                  <FaSearch />
                </button>
              )}
            </div>

            <div className="d-flex flex-wrap gap-2">
              {hasPermission("farmers:add") && (
                <button
                  className="btn btn-dark text-nowrap"
                  onClick={() => navigate("/users/add-farmer")}
                >
                  <FaPlus /> Add Farmer
                </button>
              )}

              {hasPermission("sidebar:bulk_onboard") && (
                <button
                  className="btn btn-outline-dark text-nowrap"
                  onClick={() => navigate("/bulk-onboard")}
                >
                  Bulk Onboard
                </button>
              )}
            </div>
          </div>
        </div>

        {status === 'succeeded' || status === 'loading' ? (
          <>
            <FarmersTable
              users={tableData}
              startIndex={
                isClientSidePagination
                  ? indexOfFirst + 1
                  : (currentPage - 1) * 15 + 1
              }
            />

            {/* SUPPORT pagination (Server Side) */}
            {role === 'SUPPORT' && (
              <SupportPagination
                currentPage={currentPage}
                totalPages={totalPages}
                setCurrentPage={setCurrentPage}
                fetchData={fetchUsers}
                searchKeyword={search}
              />
            )}

            {/* PARTNER & FIELD OFFICER pagination (Client Side) */}
            {isClientSidePagination && (
              <PartnerPagination
                currentPage={clientPage}
                totalPages={Math.ceil(
                  filteredClientUsers.length / itemsPerPage
                )}
                onPageChange={setClientPage}
                onItemsPerPageChange={setItemsPerPage}
              />
            )}
          </>
        ) : (
          <h3>Loading...</h3>
        )}
      </div>
    </Wrapper>
  );
};

export default Users;